import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { EmployeeForStaffingCalendar } from 'src/store/selectors';
import './d-calendar-legend.js';
import './d-staffing-calendar-table.js';
import { StaffingGroupsDialog } from 'src/pages/staffing-page/staffing-groups-dialog.js';
import { sortBy } from 'lodash';
import { ResponsiveElement } from 'src/library/elements/responsive-container.js';
import type { EmployeeWithStaffGroup } from './employee-with-staff-group';
import type { StaffingGroupsResult } from './staffing-groups-result';
import type { EmployeeGroup } from './employee-group';
import type { StaffingCalendar } from './staffing-calendar';
import type { StartOfDayTime } from './start-of-day-time';
import type { EndOfDayTime } from './end-of-day-time';
import type { ZoomUnit } from './zoom-unit';
import type { TimekeepingBeforeEmploymentStartWarning } from 'src/utilities/time-balance/types';

/**
 *
 * Migration: calendarium Months, weeks and days are not selectors. Depending on performance they may need to be reimplemented as selectors.
 */
@customElement('d-staffing-calendar')
export class DStaffingCalendar extends ResponsiveElement {
  static readonly styles = css`
    :host {
      display: block;
      background: var(--backgroundGray);
    }
    .no-horizontal-scroll > div > div {
      box-sizing: border-box;
      position: -webkit-sticky;
      position: sticky;
      left: 0;
      padding-top: 20px;
    }
  `;

  @property({ type: Number })
  width = 0;
  @property({ type: Number })
  appWidth = 1000;
  @property({ type: Number })
  pageWidth = 1000;
  @property({ type: Number })
  calendarWidth = 0;
  @property({ type: Number })
  paddingLeft = 30;
  @property({ type: Number })
  paddingTop = 20;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Number })
  scrollLeft = 0;
  @property({ type: Array })
  employeeGroups: EmployeeGroup[] = [];
  @property({ type: String })
  year = '';
  @property({ type: Array })
  employees: EmployeeForStaffingCalendar[] = [];
  @property({ type: Object })
  timekeepingBeforeEmploymentStartWarning?: TimekeepingBeforeEmploymentStartWarning;
  @property({ type: Object })
  employeeCalendarData!: StaffingCalendar;
  @property({ type: String })
  zoom: ZoomUnit = 'day';
  @property({ type: Object })
  calendarDayRange!: { start: StartOfDayTime; end: EndOfDayTime };
  @property({ type: Array })
  groupGraphData: string[][] = [];
  @property({ type: Boolean })
  currentUserHasAccess = false;
  @property({ type: Boolean })
  leavePeriodEditRestriction = true;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: String })
  currentEmployeeUuid?: string;

  private get employeesWithStaffGroup(): EmployeeWithStaffGroup[] {
    const employeesByUuid = new Map(this.employees.map((e) => [e.uuid, e]));
    const list: EmployeeWithStaffGroup[] = [];
    this.employeeGroups.forEach((group) => {
      group.items.forEach((item) => {
        const employee = employeesByUuid.get(item.uuid);
        if (!employee) {
          return;
        }
        list.push({
          uuid: employee.uuid,
          name: employee.name,
          staffGroup: employee.staffGroup,
          ended: group.ended === true,
        });
      });
    });
    return sortBy(list, [(item) => item.name]);
  }

  onResize() {
    this.width = this.offsetWidth;
    this.dispatchEvent(
      new CustomEvent('width-changed', {
        bubbles: true,
        composed: true,
        detail: this.offsetWidth,
      }),
    );
  }

  _onCalenderWidthChanged(e) {
    e.stopPropagation();
    this.calendarWidth = e.detail;
    this.dispatchEvent(
      new CustomEvent('calendar-width-changed', {
        bubbles: true,
        composed: true,
        detail: this.calendarWidth,
      }),
    );
  }

  render() {
    return html`
      <d-staffing-calendar-table
        .currentUserHasAccess=${this.currentUserHasAccess}
        .appWidth=${this.appWidth}
        .pageWidth=${this.appWidth}
        .contentStickyTop=${this.contentStickyTop}
        .scrollLeft=${this.scrollLeft}
        .paddingLeft=${this.paddingLeft}
        .paddingTop=${this.paddingTop}
        .employeeGroups=${this.employeeGroups}
        .year=${this.year}
        .employees=${this.employees}
        .employeeCalendarData=${this.employeeCalendarData}
        .dayStart=${this.calendarDayRange.start}
        .dayEnd=${this.calendarDayRange.end}
        .zoom=${this.zoom}
        .employeesForStaffingCalendar=${this.employees}
        .timekeepingBeforeEmploymentStartWarning=${this.timekeepingBeforeEmploymentStartWarning}
        .groupGraphData=${this.groupGraphData}
        @width-changed=${(e) => this._onCalenderWidthChanged(e)}
        @employee-day-click=${this.onEmployeeDayClick}
        @employee-groups=${this.onEmployeeGroups}
      ></d-staffing-calendar-table>
    `;
  }

  private async onEmployeeGroups(e: CustomEvent) {
    e.stopPropagation();
    const result: StaffingGroupsResult = await StaffingGroupsDialog.open({ employees: this.employeesWithStaffGroup });
    if (result.action === 'done') {
      this.dispatchEvent(
        new CustomEvent('update-employee-staff-groups', {
          bubbles: true,
          composed: true,
          detail: { value: result.changedEmployees },
        }),
      );
    }
  }

  private onEmployeeDayClick(e: CustomEvent<{ uuid: string; date: string }>) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('employee-day-click', {
        bubbles: true,
        composed: true,
        detail: { uuid: e.detail.uuid, date: e.detail.date },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-staffing-calendar': DStaffingCalendar;
  }
}
